import React from 'react'

// Hooks
import { useFlag } from 'containers/flags'

// Compoments
import { Tooltip } from '@sketch/components'

// Local Components
import { default as AccessLevelSelect } from '../AccessLevelSelect'
import { default as Option } from '../LevelOption'

// Styles
import {
  Checkbox,
  FullSeparator as Separator,
  Header,
  StyledPill,
} from '../AccessLevelSelect.styles'

// Types
import type { AccessLevelSelectProps } from '../types'
// Usage of PublicAccessLevel and PrivateAccessLevel will not break the build
// eslint-disable-next-line no-restricted-imports
import { PublicAccessLevel } from '@sketch/gql-types/expansive'

interface PublicPermissionsAccessSelectProps
  extends AccessLevelSelectProps<PublicAccessLevel, PublicAccessLevel> {
  levelDisabled?: boolean
  published?: boolean
  publicationDisabled?: boolean
  setPublicationEnabled?: (e: React.ChangeEvent<HTMLInputElement>) => void
  optionsDisabled?: boolean
}

// Helpers
const PILL_VARIANT = { kind: 'secondary' } as const

export const PublicPermissionsAccessSelect = (
  props: PublicPermissionsAccessSelectProps
) => {
  const {
    level,
    setLevel,
    setCommentsEnabled,
    commentsEnabled,
    setInspectEnabled,
    inspectEnabled,
    hideCommentLabel,
    optionsDisabled,
    published,
    publicationDisabled,
    setPublicationEnabled,
    levelDisabled,
    disabled,
  } = props

  const isPublishToCommunityEnabled = useFlag('publish-community')

  return (
    <AccessLevelSelect
      title="Public Access"
      level={level}
      setLevel={setLevel}
      commentsEnabled={commentsEnabled}
      hideCommentLabel={hideCommentLabel}
      noAccessRedColor={false}
      disabled={disabled}
    >
      <Tooltip
        disabled={!levelDisabled}
        placement="right"
        spacing="-50px"
        content="Only Admins can change this setting"
      >
        <Option
          data-testid="public-access-level-none"
          level="NONE"
          label="No Access"
          help="Only people invited to the document have access"
          disabled={levelDisabled}
        />
      </Tooltip>
      <Option
        data-testid="public-access-level-view"
        level="VIEW"
        label="View"
        help="View document in the web app"
        disabled={levelDisabled}
      />
      <Separator />
      <Checkbox
        data-testid="inspect-enabled"
        label="Can download and inspect"
        onChange={setInspectEnabled}
        checked={inspectEnabled}
        disabled={optionsDisabled}
      />
      <Checkbox
        data-testid="comments-enabled"
        label="Can comment"
        onChange={setCommentsEnabled}
        checked={commentsEnabled}
        disabled={optionsDisabled}
      />

      {isPublishToCommunityEnabled && (
        <>
          <Separator />
          <Header>
            Public Profile <StyledPill variant={PILL_VARIANT}>Admin</StyledPill>
          </Header>
          <Checkbox
            data-testid="community-enabled"
            label="Publish to profile"
            help="Can be publicly viewed and downloaded"
            onChange={setPublicationEnabled}
            checked={published}
            disabled={publicationDisabled}
          />
        </>
      )}
    </AccessLevelSelect>
  )
}
